<template>
  <div class="p-6">
    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-gray-900">
        {{ type.charAt(0).toUpperCase() + type.slice(1) }} Changes
      </h1>
      <button
        @click="goBack"
        class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition duration-200"
      >
        Back to List
      </button>
    </div>

    <!-- Error Message -->
    <div
      v-if="error"
      class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
      role="alert"
    >
      <strong class="font-bold">Error!</strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>

    <!-- Loading Message -->
    <div v-if="isLoading" class="text-gray-600 mb-4">Loading changes...</div>

    <!-- Changes Details -->
    <div
      v-if="!isLoading && !error && details && fullObject"
      class="bg-white shadow-md rounded-lg p-6"
    >
      <div class="grid grid-cols-1 gap-4">
        <!-- Basic Information -->
        <div>
          <h2 class="text-xl font-semibold mb-2">Basic Information</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <p class="text-sm text-gray-600">ID</p>
              <p class="font-medium">{{ details.id }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600">Name</p>
              <p class="font-medium">{{ fullObject.name }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-600">Action</p>
              <p :class="['font-medium', getActionClass(details.action)]">
                {{
                  details.action.charAt(0).toUpperCase() +
                  details.action.slice(1)
                }}
              </p>
            </div>
            <div>
              <p class="text-sm text-gray-600">Created</p>
              <p class="font-medium">
                {{ formatDateTime(fullObject.created_date) }}
              </p>
            </div>
            <div>
              <p class="text-sm text-gray-600">Last Modified</p>
              <p class="font-medium">
                {{ formatDateTime(fullObject.last_modified) }}
              </p>
            </div>
            <div>
              <p class="text-sm text-gray-600">Status</p>
              <div class="flex space-x-2">
                <span
                  :class="[
                    'px-2 py-1 rounded-full text-sm',
                    fullObject.is_approved
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800',
                  ]"
                >
                  {{ fullObject.is_approved ? "Approved" : "Pending Approval" }}
                </span>
                <span
                  v-if="fullObject.in_maintenance"
                  class="px-2 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                >
                  In Maintenance
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Current Configuration -->
        <div>
          <h2 class="text-xl font-semibold mb-4">Current Configuration</h2>
          <div class="bg-gray-100 p-4 rounded-md">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                v-for="(value, key) in filteredObjectData"
                :key="key"
                class="bg-white p-3 rounded-md"
              >
                <p class="text-sm text-gray-600 mb-1">
                  {{ formatFieldName(key) }}
                </p>
                <p class="font-medium">{{ formatValue(value) }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Changes Section -->
        <div v-if="details.differences">
          <h2 class="text-xl font-semibold mb-4">Pending Changes</h2>
          <div class="bg-gray-100 p-4 rounded-md overflow-x-auto">
            <div
              v-for="(change, key) in details.differences"
              :key="key"
              class="mb-6 last:mb-0"
            >
              <h3 class="font-semibold text-lg mb-2">
                {{ formatFieldName(key) }}
              </h3>
              <div class="flex items-center">
                <div
                  class="flex-1 bg-white p-3 rounded-l-md border-l-4 border-red-500"
                >
                  <p class="text-sm text-gray-600 mb-1">Current Value:</p>
                  <p class="font-medium">{{ formatValue(change[0]) }}</p>
                </div>
                <div class="mx-4 text-gray-400">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </div>
                <div
                  class="flex-1 bg-white p-3 rounded-r-md border-r-4 border-green-500"
                >
                  <p class="text-sm text-gray-600 mb-1">Proposed Value:</p>
                  <p class="font-medium">{{ formatValue(change[1]) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- New Configuration Section -->
        <div v-if="details.pending_changes">
          <h2 class="text-xl font-semibold mb-4">New Configuration</h2>
          <div class="bg-gray-100 p-4 rounded-md">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                v-for="(value, key) in details.pending_changes"
                :key="key"
                class="bg-white p-3 rounded-md"
              >
                <h3 class="text-sm text-gray-600 mb-1">
                  {{ formatFieldName(key) }}
                </h3>
                <p class="font-medium">{{ formatValue(value) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { getSiteChanges, getProbeChanges } from "@/services/changesService";
import { getProbeById } from "@/services/probeService";
import { getSiteById } from "@/services/siteService";

export default {
  name: "ChangesDetails",
  setup() {
    const route = useRoute();
    const router = useRouter();
    const details = ref(null);
    const fullObject = ref(null);
    const error = ref(null);
    const isLoading = ref(false);
    const type = ref(route.params.type);

    const filteredObjectData = computed(() => {
      if (!fullObject.value) return {};

      // Exclude fields we don't want to show in the current configuration
      const excludedFields = [
        "id",
        "name",
        "created_date",
        "last_modified",
        "is_approved",
        "in_maintenance",
        "pendingprobe",
        "orginalprobe",
        "pending",
      ];

      return Object.fromEntries(
        Object.entries(fullObject.value).filter(
          ([key]) => !excludedFields.includes(key)
        )
      );
    });

    const fetchDetails = async () => {
      isLoading.value = true;
      try {
        const getChanges =
          type.value === "site" ? getSiteChanges : getProbeChanges;
        const getObjectById =
          type.value === "site" ? getSiteById : getProbeById;

        const [changesResponse, objectResponse] = await Promise.all([
          getChanges(route.params.id),
          getObjectById(route.params.id),
        ]);

        details.value = changesResponse;
        fullObject.value = objectResponse;
        error.value = null;
      } catch (err) {
        console.error(`Error fetching ${type.value} details:`, err);
        error.value = "Failed to fetch details. Please try again later.";
        details.value = null;
        fullObject.value = null;
      } finally {
        isLoading.value = false;
      }
    };

    const formatDateTime = (dateString) => {
      const options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    };

    const formatFieldName = (fieldName) => {
      return fieldName
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    };

    const formatValue = (value) => {
      if (value === null || value === undefined || value === "None") {
        return "None";
      }
      if (Array.isArray(value)) {
        return value.join(", ") || "None";
      }
      if (typeof value === "object") {
        return JSON.stringify(value, null, 2);
      }
      if (typeof value === "boolean") {
        return value ? "Yes" : "No";
      }
      return String(value);
    };

    const getActionClass = (action) => {
      switch (action) {
        case "create":
          return "text-green-600";
        case "update":
          return "text-blue-600";
        default:
          return "text-gray-600";
      }
    };

    const goBack = () => {
      router.push({ name: "Changes" });
    };

    onMounted(fetchDetails);

    return {
      details,
      fullObject,
      filteredObjectData,
      error,
      isLoading,
      type,
      formatFieldName,
      formatValue,
      formatDateTime,
      getActionClass,
      goBack,
    };
  },
};
</script>
